import React, { useState, useCallback, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useSelector } from 'react-redux';
import { selectUserParams } from '../../../store/slices/userParamsSlice';
import { usePermissions } from '../../../hooks/usePermissions';
import { useGetInternalRetailPartsListQuery, useLazyExportInterserviceToExcelQuery } from '../../../api/endpoints/parts';
import GenericTableFilter from '../../../components/common/GenericTableFilter';
import InternalRetailPartsTable from './components/InternalRetailPartsTable';
import InternalRetailPartsAddDialog from './components/InternalRetailPartsAddDialog';
import InternalRetailPartsDetailDialog from './components/InternalRetailPartsDetailDialog';
import InternalRetailSubDealerDropdown from './components/InternalRetailSubDealerDropdown';
import GenericFileExportButton from '../../../components/common/GenericFileExportButton';

// Admin bayi dropdown'unda "Tümü" seçeneği için sentinel değer (query'de null'a çevrilir)
const ADMIN_ALL = 'ALL';

const InternalRetailPartsPage = () => {
    const { th } = useLanguage();
    const userParams = useSelector(selectUserParams);
    const { hasAdminRole, hasWarrantyManagerRole } = usePermissions();
    // Sayfa ve "Ekle" butonu yalnızca yurt dışı bayiler için (isDomestic === false)
    const isForeignDealer = userParams?.isDomestic === false;
    // Admin/WarrantyManager sayfaya girebilir ve bayi dropdown'u görür
    const isAdmin = hasAdminRole() || hasWarrantyManagerRole();

    // Admin için seçili bayi (adminKunnr) - varsayılan olarak "Tümü" (ADMIN_ALL -> query'de null)
    const [adminKunnr, setAdminKunnr] = useState(ADMIN_ALL);
    // "Tümü" veya admin değilse query/export'a null gider, aksi halde seçili bayi id'si
    const adminKunnrParam = isAdmin && adminKunnr !== ADMIN_ALL ? adminKunnr : null;

    // Transfer type seçenekleri (0: Alıcı, 1: Gönderen, 2: Tümü)
    const transferTypeOptions = [
        { label: th('internalRetailParts.receiver') || 'Alıcı', value: 0 },
        { label: th('internalRetailParts.sender') || 'Gönderen', value: 1 },
        { label: th('internalRetailParts.all') || 'Tümü', value: 2 }
    ];

    // Pagination state
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 20,
        page: 1,
        sortField: null,
        sortOrder: null
    });

    // Filter state
    const [filters, setFilters] = useState({
        Kunnr: '',
        TransferType: 2
    });

    // Dialog states
    const [addDialogVisible, setAddDialogVisible] = useState(false);
    const [detailDialogVisible, setDetailDialogVisible] = useState(false);
    const [selectedTransfer, setSelectedTransfer] = useState(null);

    // Fetch list data
    const { data, isLoading, isFetching, refetch } = useGetInternalRetailPartsListQuery({
        Kunnr: filters.Kunnr || '',
        TransferType: filters.TransferType ?? 2,
        AdminKunnr: adminKunnrParam,
        PageNumber: lazyParams.page,
        PageSize: lazyParams.rows
    });

    const items = data?.items || [];
    const totalCount = data?.totalCount || 0;

    const exportParams = useMemo(
        () => ({
            Kunnr: filters.Kunnr || null,
            TransferType: filters.TransferType ?? 2,
            AdminKunnr: adminKunnrParam,
            languageId: 1
        }),
        [filters.Kunnr, filters.TransferType, adminKunnrParam]
    );

    // Page change handler
    const onPage = useCallback((event) => {
        setLazyParams(prev => ({
            ...prev,
            first: event.first,
            rows: event.rows,
            page: Math.floor(event.first / event.rows) + 1
        }));
    }, []);

    // Sort handler
    const onSort = useCallback((event) => {
        setLazyParams(prev => ({
            ...prev,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        }));
    }, []);

    // Filter handlers
    const handleApplyFilters = useCallback((newFilters) => {
        setFilters(newFilters);
        setLazyParams(prev => ({ ...prev, first: 0, page: 1 }));
    }, []);

    const handleResetFilters = useCallback(() => {
        setFilters({ Kunnr: '', TransferType: 2 });
        setLazyParams(prev => ({ ...prev, first: 0, page: 1 }));
    }, []);

    // Admin bayi dropdown değişimi ("Tümü" seçilirse ADMIN_ALL)
    const handleAdminKunnrChange = useCallback((value) => {
        setAdminKunnr(value ?? ADMIN_ALL);
        setLazyParams(prev => ({ ...prev, first: 0, page: 1 }));
    }, []);

    // Dialog handlers
    const handleAdd = useCallback(() => {
        setAddDialogVisible(true);
    }, []);

    const handleAddDialogHide = useCallback(() => {
        setAddDialogVisible(false);
    }, []);

    const handleViewDetail = useCallback((row) => {
        setSelectedTransfer(row);
        setDetailDialogVisible(true);
    }, []);

    const handleDetailDialogHide = useCallback(() => {
        setDetailDialogVisible(false);
        setSelectedTransfer(null);
    }, []);

    const handleSuccess = useCallback(() => {
        refetch();
        setAddDialogVisible(false);
    }, [refetch]);

    // Filter fields for GenericTableFilter
    const filterFields = [
        {
            field: 'TransferType',
            headerName: th('internalRetailParts.transferType') || 'Transfer Tipi',
            type: 'select',
            options: transferTypeOptions
        },
        {
            field: 'Kunnr',
            headerName: th('internalRetailParts.subDealer') || 'Alt Bayi',
            type: 'custom',
            customComponent: ({ value, onChange }) => (
                <InternalRetailSubDealerDropdown
                    value={value || null}
                    onChange={(v) => onChange(v ?? '')}
                    showClear
                    placeholder={th('internalRetailParts.selectSubDealer') || 'Alt bayi seçiniz'}
                />
            )
        }
    ];

    // Yurt içi bayiler bu sayfayı kullanamaz (Admin/WarrantyManager erişebilir)
    if (!isForeignDealer && !isAdmin) {
        return (
            <div className="card">
                <div className="flex justify-content-between align-items-center mb-4">
                    <h1 className="m-0">{th('internalRetailParts.pageTitle') || 'Dahili Parça Transferi'}</h1>
                </div>
                <Message
                    severity="info"
                    text={th('internalRetailParts.onlyForeignDealers') || 'Bu sayfa yalnızca yurt dışı bayiler için kullanılabilir.'}
                />
            </div>
        );
    }

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <div className="flex align-items-center gap-3">
                    <h1 className="m-0">{th('internalRetailParts.pageTitle') || 'Dahili Parça Transferi'}</h1>
                    {isAdmin && (
                        <InternalRetailSubDealerDropdown
                            value={adminKunnr}
                            onChange={handleAdminKunnrChange}
                            extraOptions={[{ label: th('internalRetailParts.all') || 'Tümü', value: ADMIN_ALL }]}
                            placeholder={th('internalRetailParts.selectDealer') || 'Bayi seçiniz'}
                            className="w-15rem"
                        />
                    )}
                </div>
                <div className="flex gap-2">
                    <GenericFileExportButton
                        useExportQuery={useLazyExportInterserviceToExcelQuery}
                        exportParams={exportParams}
                        buttonLabel={th('common.export')}
                        buttonClassName="p-button-success"
                        icon="pi pi-file-excel"
                        defaultFileName="ServisArasiTransferler"
                    />
                    {isForeignDealer && (
                        <Button
                            label={th('common.add') || 'Ekle'}
                            icon="pi pi-plus"
                            severity="success"
                            onClick={handleAdd}
                        />
                    )}
                </div>
            </div>

            <GenericTableFilter
                filterFields={filterFields}
                initialFilters={filters}
                onApplyFilters={handleApplyFilters}
                onResetFilters={handleResetFilters}
            />

            <div className="mt-3">
                <InternalRetailPartsTable
                    th={th}
                    data={items}
                    totalRecords={totalCount}
                    lazyParams={lazyParams}
                    onPage={onPage}
                    onSort={onSort}
                    loading={isLoading || isFetching}
                    onViewDetail={handleViewDetail}
                />
            </div>

            <InternalRetailPartsAddDialog
                visible={addDialogVisible}
                onHide={handleAddDialogHide}
                onSuccess={handleSuccess}
            />

            <InternalRetailPartsDetailDialog
                visible={detailDialogVisible}
                onHide={handleDetailDialogHide}
                transfer={selectedTransfer}
            />
        </div>
    );
};

export default InternalRetailPartsPage;

